import { Box, Typography } from '@mui/material';
import * as Sentry from '@sentry/nextjs';
import Fuse from 'fuse.js';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';
import { useDebounce } from 'use-debounce';

import Button from 'components/Button';
import CustomSearchbar from 'components/CustomSearchBar';
import ErrorText from 'components/ErrorText';

import { useAppDispatch, useAppSelector } from 'redux-store';
import assignMembersToDeviceThunk from 'redux-store/thunks/organizationOverview/assignMembersToDeviceThunk';
import getOrganizationByIdThunk from 'redux-store/thunks/organizations/getOrganizationByIdThunk';

import type { AssignMembersToDevicesModalStageProps } from '..';
import MemberList from './MemberList';

const AssignMembersToDevices: FC<AssignMembersToDevicesModalStageProps> = ({
  state,
  setState,
  onCloseProxy,
  onClose,
  organizationId,
  deviceId,
  selectedDevices,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [searchString, setSearchString] = useState('');
  const [searchResults, setSearchResults] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);

  const organizationName = useAppSelector(
    s => s.organizations.selectedOrganization.organization?.name,
  );

  const organizationMembers = useAppSelector(
    s => s.organizations.selectedOrganization.organization?.users ?? [],
  );

  const organizationDevices = useAppSelector(
    s => s.organizations.selectedOrganization.organization?.devices ?? [],
  );

  const membersAssignedToDevice = useMemo(
    () => organizationDevices.find(org => org.id === deviceId)?.users || [],
    [organizationDevices, deviceId],
  );

  const unassignedMembers = organizationMembers.filter(
    member => !membersAssignedToDevice.includes(member.id),
  );

  const fuse = useMemo(
    () =>
      new Fuse(unassignedMembers, {
        keys: ['email'],
        threshold: 0.4,
      }),
    [unassignedMembers],
  );

  const handleSearch = useCallback((): void => {
    if (!searchString) {
      setSearchResults(null);

      return;
    }

    const results = fuse.search(searchString);

    setSearchResults(results.map(result => result.item.email));
  }, [fuse, searchString]);

  const [debouncedSearch] = useDebounce(searchString, 500);

  useEffect(() => {
    if (debouncedSearch) {
      handleSearch();
    }
  }, [debouncedSearch, handleSearch]);

  const handleClearSearch = useCallback((): void => {
    setSearchString('');
    setSearchResults(null);
  }, []);

  const selectedMemberIds = useMemo(() => state.members, [state.members]);
  const setSelectedMemberIds = useCallback(
    (members: number[]) => {
      setState(prev => ({ ...prev, members }));
    },
    [setState],
  );

  const filteredMembers = useMemo(
    () =>
      searchResults
        ? unassignedMembers.filter(member =>
            searchResults.includes(member.email),
          )
        : unassignedMembers,
    [unassignedMembers, searchResults],
  );

  const handleAssignMembersToDevices = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();

      const devicesToAssign = deviceId ? [deviceId] : selectedDevices;

      if (!organizationId || devicesToAssign.length === 0) {
        return;
      }

      try {
        setLoading(true);

        const results = await Promise.all(
          devicesToAssign.map(id =>
            dispatch(
              assignMembersToDeviceThunk({
                organizationId: organizationId,
                deviceId: id,
                data: {
                  users: selectedMemberIds,
                },
              }),
            ).unwrap(),
          ),
        );

        const allSuccess = results.every(result => result.success);

        if (allSuccess) {
          await dispatch(
            getOrganizationByIdThunk({ organizationId: organizationId }),
          ).unwrap();

          setState({ members: [], stage: 'success' });
          setTimeout(() => {
            setState({ members: [], stage: 'members' });
            onClose();
          }, 1250);
        } else {
          setError(
            t(
              'common:pages.organization_overview.assign_members_to_device.cannot_assign',
            ),
          );
        }
      } catch (e) {
        Sentry.captureException(e, {
          extra: { devicesToAssign, selectedMemberIds },
          tags: { page: 'AssignMembersToDevicesModal' },
        });
      } finally {
        setLoading(false);
      }
    },
    [
      deviceId,
      selectedDevices,
      organizationId,
      dispatch,
      selectedMemberIds,
      setState,
      onClose,
      t,
    ],
  );

  return (
    <>
      <Box
        sx={{
          height: '45vh',
          overflow: 'auto',
          paddingBottom: 5,
          width: '100%',
        }}
      >
        <Box mb={4}>
          <Typography
            variant="h2"
            mb={2}
            textAlign="center"
            data-testid="assign-members-to-device-title"
          >
            {t(
              'common:pages.organization_overview.assign_members_to_device.title',
            )}
          </Typography>
        </Box>
        <Typography variant="h3" mb={1} data-testid="org-members-text">
          {t(
            `common:pages.organization_overview.assign_members_to_device.org_members`,
            { organization: organizationName },
          )}
        </Typography>
        <Box>
          <Box mb={3} width="100%">
            <CustomSearchbar
              search={searchString}
              setSearch={setSearchString}
              hasSearched={searchString.length > 0}
              handleSearch={handleSearch}
              handleClearSearch={handleClearSearch}
              testId="assign-members-to-device-search-bar"
            />
          </Box>
          {filteredMembers.length > 0 ? (
            <Box>
              {filteredMembers.map(item => (
                <MemberList
                  key={item.email}
                  member={item}
                  organizationId={organizationId}
                  deviceId={deviceId}
                  userId={null}
                  selectedMembers={selectedMemberIds}
                  setSelectedMembers={setSelectedMemberIds}
                />
              ))}
            </Box>
          ) : (
            <Typography
              variant="body1"
              textAlign="center"
              data-testid="no-members-found-text"
            >
              {t(
                'common:pages.organization_overview.assign_members_to_device.no_member_found',
              )}
            </Typography>
          )}
          <ErrorText error={error} />
        </Box>
      </Box>
      <Box
        width="100%"
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        mt={2}
        gap={2}
      >
        <Button
          variant="flat"
          color="neutral"
          size="large"
          fullWidth
          onClick={onCloseProxy}
          data-testid="modal-cancel-button"
        >
          {t('common:cancel')}
        </Button>
        <Button
          variant="flat"
          size="large"
          fullWidth
          disabled={isLoading}
          onClick={handleAssignMembersToDevices}
          data-testid="modal-save-button"
        >
          {t('common:save')}
        </Button>
      </Box>
    </>
  );
};

export default AssignMembersToDevices;
